// NovaWidgetVanilla.ts - Vanilla JS implementation of the Nova Chat Widget

import type { NovaWidgetConfig, NovaMessage, NovaSession, NovaError, NovaWidgetAPI } from './types';
import { DEFAULT_CONFIG, DEFAULT_STARTER_PROMPTS } from './types';
import { NovaAPIClient } from './api';
import { NovaWebSocket, isWebSocketSupported } from './websocket';
import { NovaAuthClient, NovaLoginModal, type AuthUser, type UsageStatus } from './auth';
import { NovaMascot, CELEBRATE_MS, TALK_MS, type NovaMood } from './mascot';
import './styles/glassmorphism.css';
import './styles/loginModal.css';
import './styles/accountMenu.css';

// SVG Icons as Data URIs (more reliable than inline SVG)
const LOGIN_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/%3E%3Ccircle cx='12' cy='7' r='4'/%3E%3C/svg%3E";
const SEND_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z' fill='%23FFFFFF'/%3E%3C/svg%3E";
const MINIMIZE_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='4 14 10 14 10 20'/%3E%3Cpolyline points='20 10 14 10 14 4'/%3E%3Cline x1='14' y1='10' x2='21' y2='3'/%3E%3Cline x1='3' y1='21' x2='10' y2='14'/%3E%3C/svg%3E";

// Error codes after which Nova's face goes to sleep (until the next answer).
const SLEEP_ERRORS = new Set(['rate_limit', 'cooldown', 'login_required']);

const ICONS = {
  login: `<img src="${LOGIN_ICON}" alt="Login" width="18" height="18" style="display:block" />`,
  send: `<img src="${SEND_ICON}" alt="Send" width="18" height="18" style="display:block" />`,
  minimize: `<img src="${MINIMIZE_ICON}" alt="Minimize" width="24" height="24" style="display:block" />`,
  thumbUp: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>`,
  thumbDown: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10zM17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>`,
  error: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
};

export class NovaWidgetVanilla implements NovaWidgetAPI {
  private config: NovaWidgetConfig;
  // @ts-ignore - Reserved for future use
  private _container: HTMLElement | null = null;
  private apiClient: NovaAPIClient | null = null;
  private wsClient: NovaWebSocket | null = null;
  private authClient: NovaAuthClient | null = null;
  private loginModal: NovaLoginModal | null = null;
  private session: NovaSession | null = null;
  private currentUser: AuthUser | null = null;
  private messages: NovaMessage[] = [];
  private _isOpen: boolean = false;
  private isLoading: boolean = false;
  private isTyping: boolean = false;
  // @ts-ignore - State tracking for future use
  private isInitializing: boolean = true;
  private isWsConnected: boolean = false;

  // Account menu state
  private accountMenu: HTMLElement | null = null;
  private isAccountMenuOpen: boolean = false;

  // DOM elements
  private root: HTMLElement | null = null;
  private chatWindow: HTMLElement | null = null;
  private messagesContainer: HTMLElement | null = null;
  private inputElement: HTMLTextAreaElement | null = null;
  private sendButton: HTMLButtonElement | null = null;
  private fabButton: HTMLButtonElement | null = null;
  private loginButton: HTMLButtonElement | null = null;
  // True when this site gives signed-out visitors no messages at all.
  private signInRequired = false;

  // Nova's face: one element per place, the mood swaps its source.
  private headerMascot: NovaMascot | null = null;
  private fabMascot: NovaMascot | null = null;
  private typingMascot: NovaMascot | null = null;
  private welcomeMascot: NovaMascot | null = null;
  // Why the header face is asleep (an error, or the live connection is down).
  private asleep: 'error' | 'offline' | null = null;

  constructor(config: NovaWidgetConfig) {
    this.config = { ...DEFAULT_CONFIG, ...config } as NovaWidgetConfig;
    this.init();
  }

  private async init(): Promise<void> {
    if (!this.config.apiKey) {
      console.error('[NovaWidget] API key is required');
      return;
    }

    // Create API client
    this.apiClient = new NovaAPIClient(this.config.apiKey, this.config.apiUrl);

    // Create Auth client
    this.authClient = new NovaAuthClient(this.config.apiKey, this.config.apiUrl);

    // Create DOM structure
    this.createDOM();

    // Initialize session
    await this.initSession();

    // Initialize auth client with session
    if (this.session && this.authClient) {
      this.authClient.setSessionId(this.session.sessionId);
      this.initLoginModal();
      await this.checkAuthStatus();
    }

    // Set initial state
    if (this.config.defaultOpen) {
      this.open();
    }
  }

  private initLoginModal(): void {
    if (!this.authClient) return;

    this.loginModal = new NovaLoginModal({
      authClient: this.authClient,
      onSuccess: async (user) => {
        this.currentUser = user;
        this.updateLoginButton();

        // Refresh auth status to ensure session is properly linked
        await this.checkAuthStatus();

        // Clear any login-required banners
        this.messagesContainer?.querySelector('.nova-login-banner')?.remove();

        // Add a welcome message from NOVA
        const welcomeMessage: NovaMessage = {
          id: `assistant_${Date.now()}`,
          role: 'assistant',
          content: `Welcome back, ${user.displayName}! Ask me anything.`,
          timestamp: new Date(),
          feedback: null,
        };
        this.messages.push(welcomeMessage);
        this.renderMessages();
      },
      onError: (error) => {
        this.showError(error.message);
        this.config.onError?.(error);
      },
      onClose: () => {
        // Modal closed
      },
    });
  }

  private async checkAuthStatus(): Promise<void> {
    if (!this.authClient) return;

    try {
      const status = await this.authClient.getStatus();
      if (status.authenticated && status.user) {
        this.currentUser = status.user;
        this.signInRequired = false;
        this.updateLoginButton();
      } else {
        this.signInRequired = status.anonymousMessagesLimit === 0;
      }
      // Re-render so the sign-in call to action appears / disappears.
      if (!this.isInitializing) this.renderMessages();
    } catch {
      // Ignore errors - user is not logged in
    }
  }

  private updateLoginButton(): void {
    if (!this.loginButton) return;

    if (this.currentUser) {
      // Show user avatar or initial
      const initial = this.currentUser.displayName?.charAt(0).toUpperCase() || 'U';
      const safeAvatar = this.safeAvatarUrl(this.currentUser.avatar);
      const safeName = this.escapeHtml(this.currentUser.displayName || 'User');
      if (safeAvatar) {
        this.loginButton.innerHTML = `<img src="${safeAvatar}" alt="${safeName}" style="width:18px;height:18px;border-radius:50%;object-fit:cover" />`;
      } else {
        this.loginButton.innerHTML = `<span style="font-size:12px;font-weight:600">${this.escapeHtml(initial)}</span>`;
      }
      this.loginButton.title = `Logged in as ${safeName}`;
      this.loginButton.classList.add('nova-chat__login--active');
    } else {
      this.loginButton.innerHTML = ICONS.login;
      this.loginButton.title = 'Login';
      this.loginButton.classList.remove('nova-chat__login--active');
    }
  }

  private createDOM(): void {
    // Create root container
    this.root = document.createElement('div');
    this.root.className = `nova-widget-root nova-widget-root--${this.config.position || 'bottom-right'}`;
    if (this.config.theme === 'light') {
      this.root.classList.add('nova-widget--light');
    }
    this.root.style.setProperty('--nova-z-index', String(this.config.zIndex || 999999));

    // Create chat window
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = 'nova-chat';
    this.chatWindow.innerHTML = `
      <div class="nova-chat__header">
        <div class="nova-chat__avatar"></div>
        <div class="nova-chat__title">${this.config.title || 'Nova'}</div>
        <div class="nova-chat__status">
          <span class="nova-chat__status-dot"></span>
          Online
        </div>
        <button class="nova-chat__login" aria-label="Login">${ICONS.login}</button>
      </div>
      <div class="nova-chat__messages">
        <div class="nova-loading">
          <div class="nova-loading__spinner"></div>
        </div>
      </div>
      <form class="nova-chat__input-area">
        <div class="nova-chat__input-wrapper">
          <textarea class="nova-chat__input" placeholder="${this.config.placeholder || 'Type your message...'}" rows="1" disabled></textarea>
          <button type="submit" class="nova-chat__send" disabled aria-label="Send message">${ICONS.send}</button>
        </div>
      </form>
    `;

    // Create FAB button
    this.fabButton = document.createElement('button');
    this.fabButton.className = 'nova-fab nova-fab--pulse';
    this.fabButton.setAttribute('aria-label', 'Open chat');

    // Nova's face in the header and on the launcher
    this.headerMascot = this.newMascot('idle');
    this.chatWindow.querySelector('.nova-chat__avatar')?.appendChild(this.headerMascot.el);
    this.fabMascot = this.newMascot('idle');
    this.showFabMascot();

    // Append elements
    this.root.appendChild(this.chatWindow);
    this.root.appendChild(this.fabButton);
    document.body.appendChild(this.root);

    // Get references
    this.messagesContainer = this.chatWindow.querySelector('.nova-chat__messages');
    this.inputElement = this.chatWindow.querySelector('.nova-chat__input');
    this.sendButton = this.chatWindow.querySelector('.nova-chat__send');
    this.loginButton = this.chatWindow.querySelector('.nova-chat__login');

    // Bind events
    this.bindEvents();
  }

  private bindEvents(): void {
    // FAB click
    this.fabButton?.addEventListener('click', () => this.toggle());

    // Close button
    this.chatWindow?.querySelector('.nova-chat__close')?.addEventListener('click', () => this.close());

    // Login button click
    this.loginButton?.addEventListener('click', () => this.handleLoginClick());

    // Form submit
    this.chatWindow?.querySelector('.nova-chat__input-area')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSend();
    });

    // Input keydown (Enter to send)
    this.inputElement?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    // Input change (auto-resize)
    this.inputElement?.addEventListener('input', () => {
      if (this.inputElement) {
        this.inputElement.style.height = 'auto';
        this.inputElement.style.height = `${Math.min(this.inputElement.scrollHeight, 120)}px`;
        this.updateSendButton();
      }
    });
  }

  private handleLoginClick(): void {
    if (this.currentUser) {
      // User is logged in - toggle account menu
      this.toggleAccountMenu();
    } else {
      // User is not logged in - show login modal
      this.openLoginModal();
    }
  }

  private openLoginModal(): void {
    this.loginModal?.open();
  }

  // ═══ Account Menu Methods ═══

  private toggleAccountMenu(): void {
    if (this.isAccountMenuOpen) {
      this.closeAccountMenu();
    } else {
      this.openAccountMenu();
    }
  }

  private async openAccountMenu(): Promise<void> {
    if (!this.currentUser) return;

    if (!this.accountMenu) {
      this.createAccountMenu();
    }

    this.updateAccountMenuContent();
    this.accountMenu?.classList.add('nova-account-menu--open');
    this.isAccountMenuOpen = true;

    // Refresh usage data from server in the background
    this.checkAuthStatus().then(() => {
      if (this.isAccountMenuOpen) {
        this.updateAccountMenuContent();
      }
    }).catch(() => {});

    // Close on outside click (delayed to avoid immediate trigger)
    setTimeout(() => {
      document.addEventListener('click', this.handleOutsideClick);
    }, 0);
  }

  private closeAccountMenu(): void {
    this.accountMenu?.classList.remove('nova-account-menu--open');
    this.isAccountMenuOpen = false;
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    if (!this.accountMenu?.contains(e.target as Node) &&
        !this.loginButton?.contains(e.target as Node)) {
      this.closeAccountMenu();
    }
  };

  private createAccountMenu(): void {
    this.accountMenu = document.createElement('div');
    this.accountMenu.className = 'nova-account-menu';

    // Position relative to header
    const header = this.chatWindow?.querySelector('.nova-chat__header');
    if (header) {
      (header as HTMLElement).style.position = 'relative';
      header.appendChild(this.accountMenu);
    }
  }

  private updateAccountMenuContent(): void {
    if (!this.accountMenu || !this.currentUser) return;

    const user = this.currentUser;

    const safeAvatar = this.safeAvatarUrl(user.avatar);
    const safeName = this.escapeHtml(user.displayName || 'User');
    this.accountMenu.innerHTML = `
      <div class="nova-account-menu__header">
        <div class="nova-account-menu__avatar">
          ${safeAvatar
            ? `<img src="${safeAvatar}" alt="${safeName}" />`
            : `<span class="nova-account-menu__avatar-initial">${this.escapeHtml((user.displayName || 'U').charAt(0).toUpperCase())}</span>`
          }
        </div>
        <div class="nova-account-menu__info">
          <div class="nova-account-menu__name">${this.escapeHtml(user.displayName || 'User')}</div>
          <div class="nova-account-menu__tier">
            <span class="nova-account-menu__badge nova-account-menu__badge--${user.isPremium ? 'premium' : 'free'}">
              ${user.isPremium ? 'PRO' : 'Free'}
            </span>
          </div>
        </div>
      </div>
      ${this.renderUsage(user.usage)}
      <div class="nova-account-menu__actions">
        ${!user.isPremium ? `
          <button class="nova-account-menu__btn nova-account-menu__btn--upgrade" data-action="upgrade">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Upgrade to PRO
          </button>
        ` : ''}
        <button class="nova-account-menu__btn nova-account-menu__btn--logout" data-action="logout">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Logout
        </button>
      </div>
    `;

    // Bind action buttons
    this.accountMenu.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const action = (e.currentTarget as HTMLElement).dataset.action;
        if (action === 'logout') {
          await this.handleLogout();
        } else if (action === 'upgrade') {
          window.open(this.config.upgradeUrl || 'https://nova.zeroauthoritydao.com/premium', '_blank');
        }
        this.closeAccountMenu();
      });
    });
  }

  /**
   * The AI allowance block of the account menu: one bar per rolling window,
   * percentages only (the server never sends counts or amounts).
   */
  private renderUsage(usage?: UsageStatus | null): string {
    if (!usage) return '';

    const row = (label: string, percent: number): string => {
      const pct = Math.max(0, Math.min(100, Math.round(percent)));
      const fillClass = pct >= 100 ? 'nova-account-menu__usage-fill--critical' :
                        pct >= 80 ? 'nova-account-menu__usage-fill--warning' : '';
      return `
        <div class="nova-account-menu__usage-row">
          <div class="nova-account-menu__usage-head">
            <span>${label}</span>
            <span class="nova-account-menu__usage-value">${pct}%</span>
          </div>
          <div class="nova-account-menu__usage-bar" role="progressbar" aria-label="${label} usage" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}">
            <div class="nova-account-menu__usage-fill ${fillClass}" style="width: ${pct}%"></div>
          </div>
        </div>`;
    };

    const note = usage.limited
      ? `Used up for ${usage.limited_by === 'week' ? 'this week' : 'this session'}${this.formatWait(usage.available_at)}.`
      : 'Frees up continuously as time passes.';

    return `
      <div class="nova-account-menu__usage">
        <div class="nova-account-menu__usage-label">AI usage</div>
        ${row('Session', usage.session.percent)}
        ${row('Week', usage.week.percent)}
        <div class="nova-account-menu__usage-note${usage.limited ? ' nova-account-menu__usage-note--limited' : ''}">${note}</div>
      </div>`;
  }

  /** ", back in about 2 h 10 min" for an ISO time in the future, else "". */
  private formatWait(iso: string | null): string {
    if (!iso) return '';
    const ms = new Date(iso).getTime() - Date.now();
    if (!(ms > 0)) return '';
    const mins = Math.max(1, Math.ceil(ms / 60000));
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    const text = mins < 60 ? `${mins} min`
      : hours < 24 ? (mins % 60 ? `${hours} h ${mins % 60} min` : `${hours} h`)
      : (hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`);
    return `, back in about ${text}`;
  }

  /** Take the allowance the server sent with a reply and refresh the menu. */
  private updateUsage(usage?: UsageStatus | null): void {
    if (!this.currentUser || !usage) return;
    this.currentUser.usage = usage;
    this.updateAccountMenuContent();
  }

  private async handleLogout(): Promise<void> {
    if (!this.authClient) return;

    await this.authClient.logout();
    this.currentUser = null;
    this.updateLoginButton();

    // Clear chat messages and stored session so the next user gets a fresh start
    this.clearHistory();
    localStorage.removeItem('nova_session_id');

    // Signed out again: re-check whether this site requires sign-in to chat.
    await this.checkAuthStatus();
  }

  private async initSession(): Promise<void> {
    if (!this.apiClient) return;

    try {
      const storedSessionId = localStorage.getItem('nova_session_id');
      const visitorId = this.config.visitorId || localStorage.getItem('nova_visitor_id') || undefined;

      const sessionData = await this.apiClient.initSession({
        visitorId,
        existingSessionId: storedSessionId || undefined,
        metadata: { username: this.config.username },
      });

      localStorage.setItem('nova_session_id', sessionData.sessionId);

      if (!localStorage.getItem('nova_visitor_id')) {
        localStorage.setItem('nova_visitor_id', `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      }

      this.session = sessionData;

      // Load history
      if (this.config.features?.history !== false && !sessionData.isNew) {
        const history = await this.apiClient.getHistory();
        if (history.length > 0) {
          this.messages = history;
        }
      }

      // Add greeting for new sessions
      if (sessionData.isNew && this.config.greeting) {
        this.messages = [{
          id: 'greeting',
          role: 'assistant',
          content: this.config.greeting,
          timestamp: new Date(),
          feedback: null,
        }];
      }

      this.isInitializing = false;
      this.renderMessages();
      this.enableInput();
      this.config.onReady?.(sessionData);

      // Initialize WebSocket connection if supported
      if (isWebSocketSupported()) {
        this.initWebSocket(sessionData.sessionId);
      }
    } catch (err) {
      const error = err as NovaError;
      this.isInitializing = false;
      this.showError(error.message || 'Failed to initialize');
      this.config.onError?.(error);
    }
  }

  private initWebSocket(sessionId: string): void {
    this.wsClient = new NovaWebSocket({
      apiKey: this.config.apiKey,
      sessionId,
      apiUrl: this.config.apiUrl,
      onOpen: () => {
        this.isWsConnected = true;
        if (this.asleep === 'offline') this.wake();
      },
      onClose: () => {
        this.isWsConnected = false;
        this.sleep('offline');
      },
      onMessage: (msg) => {
        const assistantMessage: NovaMessage = {
          id: msg.messageId,
          role: 'assistant',
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          feedback: null,
          suggestions: msg.suggestions,
        };
        this.messages.push(assistantMessage);
        this.isLoading = false;
        this.isTyping = false;
        this.updateUsage(msg.usage);
        this.renderMessages();
        this.updateSendButton();
        this.reactToReply(msg.celebrate === true);
        this.config.onMessageReceived?.(assistantMessage);
      },
      onTyping: (typing) => {
        this.isTyping = typing;
        this.renderMessages();
        this.syncHeaderMood();
      },
      onError: (err) => {
        this.isLoading = false;
        this.isTyping = false;
        this.renderMessages();
        if (err.code === 'max_reconnect_attempts') this.sleep('offline');
        else if (SLEEP_ERRORS.has(err.code)) this.sleep('error');
        else this.syncHeaderMood();
        if (err.code === 'login_required') {
          // Show as NOVA message + login banner (same as REST path)
          const novaMessage: NovaMessage = {
            id: `assistant_${Date.now()}`,
            role: 'assistant',
            content: err.message,
            timestamp: new Date(),
            feedback: null,
          };
          this.messages.push(novaMessage);
          this.renderMessages();
          this.showLoginRequired('Sign in to continue');
        } else if (err.code !== 'max_reconnect_attempts') {
          this.showError(err.message);
        }
        this.updateSendButton();
        this.config.onError?.(err);
      },
    });
    this.wsClient.connect();
  }

  private renderMessages(): void {
    if (!this.messagesContainer) return;

    // Signed-out visitor on a site with no anonymous allowance: they have not
    // hit any limit, so the empty state invites them to sign in.
    const needsSignIn = this.signInRequired && !this.currentUser;

    if (this.messages.length === 0) {
      this.messagesContainer.innerHTML = needsSignIn ? `
        <div class="nova-welcome">
          <div class="nova-welcome__icon" data-nova-mascot="welcome"></div>
          <p>Sign in to start chatting with Nova</p>
          <button class="nova-login-banner__btn nova-welcome__signin">Sign In</button>
        </div>
      ` : `
        <div class="nova-welcome">
          <div class="nova-welcome__icon" data-nova-mascot="welcome"></div>
          <p>Start a conversation with Nova</p>
          ${this.starterPromptsHtml()}
        </div>
      `;
      this.mountWelcomeMascot();
      this.messagesContainer.querySelector('.nova-welcome__signin')?.addEventListener('click', () => {
        this.openLoginModal();
      });
      this.bindStarterPrompts();
      return;
    }

    let html = '';

    for (const msg of this.messages) {
      const time = msg.timestamp.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      const feedbackHtml = msg.role === 'assistant' && this.config.features?.feedback !== false ? `
        <div class="nova-message__feedback">
          <button class="nova-message__feedback-btn nova-message__feedback-btn--up ${msg.feedback === 'thumbs_up' ? 'nova-message__feedback-btn--active' : ''}"
                  data-message-id="${msg.id}" data-feedback="thumbs_up" aria-label="Helpful">
            ${ICONS.thumbUp}
          </button>
          <button class="nova-message__feedback-btn nova-message__feedback-btn--down ${msg.feedback === 'thumbs_down' ? 'nova-message__feedback-btn--active' : ''}"
                  data-message-id="${msg.id}" data-feedback="thumbs_down" aria-label="Not helpful">
            ${ICONS.thumbDown}
          </button>
        </div>
      ` : '';

      const content = msg.role === 'assistant' ? this.formatMarkdown(msg.content) : this.escapeHtml(msg.content);

      html += `
        <div class="nova-message nova-message--${msg.role}">
          <div class="nova-message__bubble">${content}</div>
          <div class="nova-message__time">${time}</div>
          ${feedbackHtml}
        </div>
      `;
    }

    if (this.isTyping) {
      html += `
        <div class="nova-message nova-message--assistant">
          <div class="nova-typing nova-typing--mascot" role="status" aria-label="Nova is thinking">
            <span data-nova-mascot="typing"></span>
          </div>
        </div>
      `;
    }

    // Nothing asked yet (only the greeting): offer the starter chips under it.
    const hasAsked = this.messages.some(m => m.role === 'user');
    if (!hasAsked && !needsSignIn && !this.isLoading) {
      html += this.starterPromptsHtml();
    }

    // Follow-up questions for the latest answer, until the next message is sent.
    html += this.followUpsHtml();

    this.messagesContainer.innerHTML = html;
    this.mountTypingMascot();
    this.bindStarterPrompts();
    this.bindFollowUps();

    // Bind feedback events
    this.messagesContainer.querySelectorAll('.nova-message__feedback-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const messageId = target.dataset.messageId;
        const feedback = target.dataset.feedback as 'thumbs_up' | 'thumbs_down';
        if (messageId && feedback) {
          this.handleFeedback(messageId, feedback);
        }
      });
    });

    // Only the greeting so far: same sign-in call to action, under it.
    if (needsSignIn && !this.messages.some(m => m.role === 'user')) {
      this.showLoginRequired('Sign in to start chatting with Nova');
    }

    // Scroll to bottom
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  private starterPrompts(): string[] {
    return this.config.starterPrompts ?? DEFAULT_STARTER_PROMPTS;
  }

  private starterPromptsHtml(): string {
    const prompts = this.starterPrompts();
    if (prompts.length === 0) return '';
    const chips = prompts.map((prompt, i) =>
      `<button type="button" class="nova-starters__chip" data-starter="${i}">${this.escapeHtml(prompt)}</button>`
    ).join('');
    return `<div class="nova-starters" role="group" aria-label="Example questions">${chips}</div>`;
  }

  // A chip sends its question straight away, like typing it and pressing Enter.
  private bindStarterPrompts(): void {
    const prompts = this.starterPrompts();
    this.messagesContainer?.querySelectorAll('.nova-starters__chip[data-starter]').forEach(chip => {
      chip.addEventListener('click', (e) => {
        const prompt = prompts[Number((e.currentTarget as HTMLElement).dataset.starter)];
        if (prompt) this.sendMessage(prompt);
      });
    });
  }

  // Follow-up questions Nova suggested with its latest answer.
  private followUps(): string[] {
    if (this.isLoading || this.isTyping) return [];
    const last = this.messages[this.messages.length - 1];
    return last?.role === 'assistant' ? last.suggestions ?? [] : [];
  }

  private followUpsHtml(): string {
    const questions = this.followUps();
    if (questions.length === 0) return '';
    const chips = questions.map((question, i) =>
      `<button type="button" class="nova-starters__chip" data-followup="${i}">${this.escapeHtml(question)}</button>`
    ).join('');
    return `<div class="nova-starters nova-starters--followups" role="group" aria-label="Suggested follow-up questions">${chips}</div>`;
  }

  private bindFollowUps(): void {
    const questions = this.followUps();
    this.messagesContainer?.querySelectorAll('.nova-starters__chip[data-followup]').forEach(chip => {
      chip.addEventListener('click', (e) => {
        const question = questions[Number((e.currentTarget as HTMLElement).dataset.followup)];
        if (question) this.sendMessage(question);
      });
    });
  }

  private showError(message: string): void {
    if (!this.messagesContainer) return;

    // Remove existing error
    this.messagesContainer.querySelector('.nova-error')?.remove();

    const errorEl = document.createElement('div');
    errorEl.className = 'nova-error';
    errorEl.innerHTML = `${ICONS.error}${this.escapeHtml(message)}`;

    this.messagesContainer.appendChild(errorEl);
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  private clearError(): void {
    this.messagesContainer?.querySelector('.nova-error')?.remove();
  }

  private enableInput(): void {
    if (this.inputElement) {
      this.inputElement.disabled = false;
    }
    this.updateSendButton();
  }

  // @ts-ignore - Reserved for future use
  private _disableInput(): void {
    if (this.inputElement) {
      this.inputElement.disabled = true;
    }
    if (this.sendButton) {
      this.sendButton.disabled = true;
    }
  }

  private updateSendButton(): void {
    if (this.sendButton && this.inputElement) {
      this.sendButton.disabled = !this.inputElement.value.trim() || this.isLoading;
    }
  }

  private async handleSend(): Promise<void> {
    if (!this.inputElement) return;

    const content = this.inputElement.value.trim();
    if (!content || this.isLoading) return;

    this.clearError();

    // Add user message
    const userMessage: NovaMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
      feedback: null,
    };

    this.messages.push(userMessage);
    this.inputElement.value = '';
    this.inputElement.style.height = 'auto';
    this.isLoading = true;
    this.renderMessages();
    this.updateSendButton();
    this.config.onMessageSent?.(content);

    // Try WebSocket first, fallback to REST
    if (this.isWsConnected && this.wsClient?.isConnected()) {
      // WebSocket path - response handled by onMessage callback
      const sent = this.wsClient.sendMessage(content, { username: this.config.username, pageUrl: typeof window !== 'undefined' ? window.location.href : undefined });
      if (!sent) {
        // WebSocket send failed, fall back to REST
        await this.sendViaRest(content);
      }
      // Note: isLoading will be set to false in the onMessage callback
    } else {
      // REST fallback
      await this.sendViaRest(content);
    }
  }

  private async sendViaRest(content: string): Promise<void> {
    if (!this.apiClient) return;

    this.isTyping = true;
    this.renderMessages();
    this.syncHeaderMood();

    try {
      const response = await this.apiClient.sendMessage(content, { username: this.config.username, pageUrl: typeof window !== 'undefined' ? window.location.href : undefined });

      this.isTyping = false;

      const assistantMessage: NovaMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        feedback: null,
        suggestions: response.suggestions,
      };

      this.messages.push(assistantMessage);
      this.updateUsage(response.usage);
      this.renderMessages();
      this.reactToReply(false);
      this.config.onMessageReceived?.(assistantMessage);
    } catch (err) {
      this.isTyping = false;
      this.renderMessages();

      const error = err as NovaError & { requiresLogin?: boolean };
      if (error.requiresLogin || SLEEP_ERRORS.has(error.code)) this.sleep('error');
      else this.syncHeaderMood();

      // Handle login required error - show as NOVA chat message
      if (error.code === 'login_required' || error.requiresLogin) {
        // Add the message as a NOVA response so it appears in chat
        const novaMessage: NovaMessage = {
          id: `assistant_${Date.now()}`,
          role: 'assistant',
          content: error.message,
          timestamp: new Date(),
          feedback: null,
        };
        this.messages.push(novaMessage);
        this.renderMessages();

        // Also show the login banner with sign-in button
        this.showLoginRequired('Sign in to continue');
        this.config.onError?.(error);
        return;
      }

      // The server message already states the wait for cooldowns.
      this.showError(error.message || 'Failed to send message');
      this.config.onError?.(error);
    } finally {
      this.isLoading = false;
      this.updateSendButton();
    }
  }

  private showLoginRequired(message: string): void {
    if (!this.messagesContainer) return;

    // Remove existing login banner
    this.messagesContainer.querySelector('.nova-login-banner')?.remove();

    const bannerEl = document.createElement('div');
    bannerEl.className = 'nova-login-banner';
    bannerEl.innerHTML = `
      <div class="nova-login-banner__icon">
        ${ICONS.login}
      </div>
      <p class="nova-login-banner__text">${this.escapeHtml(message)}</p>
      <button class="nova-login-banner__btn">Sign In</button>
    `;

    // Bind click event
    bannerEl.querySelector('.nova-login-banner__btn')?.addEventListener('click', () => {
      this.openLoginModal();
    });

    this.messagesContainer.appendChild(bannerEl);
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  private async handleFeedback(messageId: string, type: 'thumbs_up' | 'thumbs_down'): Promise<void> {
    if (!this.apiClient) return;

    const message = this.messages.find(m => m.id === messageId);
    if (message) {
      message.feedback = message.feedback === type ? null : type;
      this.renderMessages();
      await this.apiClient.submitFeedback(messageId, type);
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /** Validate + escape an avatar URL before interpolating into innerHTML.
   *  Rejects javascript:, data:, vbscript: schemes and any non-string input.
   *  Returns empty string on invalid → caller should fall back to initials. */
  private safeAvatarUrl(url: unknown): string {
    if (typeof url !== 'string' || !url) return '';
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) return '';
    return this.escapeHtml(trimmed);
  }

  /** Convert basic markdown to HTML (for assistant messages) */
  private formatMarkdown(text: string): string {
    // First escape HTML to prevent XSS
    let html = this.escapeHtml(text);
    // Bold: **text**
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text* (but not inside bold)
    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    // Inline code: `text`
    html = html.replace(/`([^`]+)`/g, '<code style="background:rgba(255,255,255,0.1);padding:1px 4px;border-radius:3px;font-size:0.9em">$1</code>');
    // Links: [text](url)
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#8B5CF6;text-decoration:underline">$1</a>');
    // Line breaks
    html = html.replace(/\n/g, '<br>');
    return html;
  }

  // ═══ Nova's face ═══

  private newMascot(mood: NovaMood): NovaMascot {
    return new NovaMascot({ apiUrl: this.config.apiUrl, customUrl: this.config.avatarUrl, mood });
  }

  /** The launcher shows Nova's face while the panel is closed. */
  private showFabMascot(): void {
    if (!this.fabButton || !this.fabMascot) return;
    const icon = document.createElement('span');
    icon.className = 'nova-fab__icon nova-fab__icon--mascot';
    this.fabButton.replaceChildren(icon);
    this.fabMascot.mount(icon);
  }

  private mountWelcomeMascot(): void {
    const slot = this.messagesContainer?.querySelector('[data-nova-mascot="welcome"]');
    if (!slot) return;
    this.welcomeMascot ??= this.newMascot('idle');
    this.welcomeMascot.mount(slot);
  }

  private mountTypingMascot(): void {
    const slot = this.messagesContainer?.querySelector('[data-nova-mascot="typing"]');
    if (!slot) return;
    this.typingMascot ??= this.newMascot('thinking');
    this.typingMascot.mount(slot, true);
  }

  /** The header face at rest: thinking while Nova works, asleep after an error. */
  private syncHeaderMood(): void {
    const mood: NovaMood = this.isTyping ? 'thinking' : this.asleep ? 'sleeping' : 'idle';
    this.headerMascot?.setMood(mood);
  }

  private sleep(reason: 'error' | 'offline'): void {
    // A dropped connection does not replace an error already shown.
    if (!(reason === 'offline' && this.asleep === 'error')) this.asleep = reason;
    this.syncHeaderMood();
  }

  private wake(): void {
    this.asleep = null;
    this.syncHeaderMood();
  }

  /** A reply arrived: Nova talks briefly, or celebrates a confirmed success. */
  private reactToReply(celebrate: boolean): void {
    this.asleep = null;
    const back = () => this.syncHeaderMood();
    if (this.isTyping) {
      back();
    } else if (celebrate) {
      this.headerMascot?.playOnce('celebrating', CELEBRATE_MS, back);
    } else {
      this.headerMascot?.playOnce('talking', TALK_MS, back);
    }
    // Panel closed: the launcher celebrates once, then goes back to idle.
    if (celebrate && !this._isOpen) {
      this.fabMascot?.playOnce('celebrating', CELEBRATE_MS, () => this.fabMascot?.setMood('idle'));
    }
  }

  // Public API methods
  open(): void {
    this._isOpen = true;
    this.chatWindow?.classList.add('nova-chat--open');
    this.fabButton?.classList.remove('nova-fab--pulse');
    this.fabButton?.classList.add('nova-fab--open');
    if (this.fabButton) {
      this.fabButton.innerHTML = `<span class="nova-fab__icon">${ICONS.minimize}</span>`;
    }
    this.fabMascot?.setMood('idle');
    this.inputElement?.focus();
    this.config.onOpen?.();
  }

  close(): void {
    this._isOpen = false;
    this.chatWindow?.classList.remove('nova-chat--open');
    this.fabButton?.classList.add('nova-fab--pulse');
    this.fabButton?.classList.remove('nova-fab--open');
    this.showFabMascot();
    this.config.onClose?.();
  }

  toggle(): void {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  async sendMessage(content: string): Promise<void> {
    if (this.inputElement) {
      this.inputElement.value = content;
    }
    await this.handleSend();
  }

  getSession(): NovaSession | null {
    return this.session;
  }

  getMessages(): NovaMessage[] {
    return [...this.messages];
  }

  clearHistory(): void {
    this.messages = this.config.greeting ? [{
      id: 'greeting',
      role: 'assistant',
      content: this.config.greeting,
      timestamp: new Date(),
      feedback: null,
    }] : [];
    this.renderMessages();
  }

  updateConfig(newConfig: Partial<NovaWidgetConfig>): void {
    this.config = { ...this.config, ...newConfig };
    // Re-render with new config
    if (this.root) {
      this.root.className = `nova-widget-root nova-widget-root--${this.config.position || 'bottom-right'}`;
      if (this.config.theme === 'light') {
        this.root.classList.add('nova-widget--light');
      }
    }
  }

  destroy(): void {
    this.wsClient?.disconnect();
    for (const mascot of [this.headerMascot, this.fabMascot, this.typingMascot, this.welcomeMascot]) mascot?.destroy();
    this.headerMascot = this.fabMascot = this.typingMascot = this.welcomeMascot = null;
    this.loginModal?.destroy();
    // Clean up account menu
    this.closeAccountMenu();
    document.removeEventListener('click', this.handleOutsideClick);
    this.accountMenu?.remove();
    this.accountMenu = null;
    // Clean up DOM
    this.root?.remove();
    this.root = null;
    this.chatWindow = null;
    this.messagesContainer = null;
    this.inputElement = null;
    this.sendButton = null;
    this.fabButton = null;
    this.loginButton = null;
    this.session = null;
    this.currentUser = null;
    this.messages = [];
  }

  isOpen(): boolean {
    return this._isOpen;
  }
}

// Global initialization function for CDN usage
export function initNovaWidget(config: NovaWidgetConfig): NovaWidgetAPI {
  return new NovaWidgetVanilla(config);
}

// Auto-attach to window for CDN usage
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).NovaWidget = {
    init: initNovaWidget,
    NovaWidgetVanilla,
  };
}

export default NovaWidgetVanilla;
